import React, { useEffect, useState } from "react";
import {
    Link,
    useLocation,
    useNavigate,
} from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { Divider, message, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import axios from "axios";
import PhoneInput, {
    isValidPhoneNumber,
    parsePhoneNumber,
} from "react-phone-number-input";
import { GoogleLogin } from "@react-oauth/google";
import HeadLogo from "../Assets/Group 619.svg";
import { checkEmailExists } from "../services/auth";
import "react-toastify/dist/ReactToastify.css";
import "react-phone-number-input/style.css";

const POST_AUTH_REDIRECT_KEY = "post_auth_redirect";
const AUTH_SOURCE_KEY = "auth_source";
const PENDING_GOOGLE_SETUP_KEY =
    "pending_google_business_setup";

const isSafeInternalRedirect = (value) => {
    if (!value) return false;
    return value.startsWith("/") && !value.startsWith("//");
};

const saveMiniToolRedirect = (search) => {
    const params = new URLSearchParams(search);
    const source = params.get("source");
    const redirect = params.get("redirect");

    if (
        source === "mini-tools" &&
        isSafeInternalRedirect(redirect)
    ) {
        sessionStorage.setItem(AUTH_SOURCE_KEY, source);
        sessionStorage.setItem(
            POST_AUTH_REDIRECT_KEY,
            redirect
        );
    }
};

const getPostAuthRedirect = (fallback = "/Dashboard") => {
    const redirect = sessionStorage.getItem(
        POST_AUTH_REDIRECT_KEY
    );

    return isSafeInternalRedirect(redirect)
        ? redirect
        : fallback;
};

const readPendingGoogleSetup = () => {
    try {
        const storedValue = sessionStorage.getItem(
            PENDING_GOOGLE_SETUP_KEY
        );

        if (!storedValue) {
            return null;
        }

        const parsedValue = JSON.parse(storedValue);

        if (
            !parsedValue?.googleSetupToken ||
            !parsedValue?.user?.email_id
        ) {
            sessionStorage.removeItem(
                PENDING_GOOGLE_SETUP_KEY
            );
            return null;
        }

        return parsedValue;
    } catch (error) {
        console.warn(
            "Could not restore pending Google signup.",
            error
        );

        sessionStorage.removeItem(
            PENDING_GOOGLE_SETUP_KEY
        );

        return null;
    }
};

const getInitialDetails = (pendingGoogleSetup) => {
    const googleUser = pendingGoogleSetup?.user;

    return {
        firstname: googleUser?.first_name || "",
        lastname: googleUser?.last_name || "",
        email: googleUser?.email_id || "",
        password: "",
        company_name: "",
        website: "",
    };
};

const getNationalPhoneNumber = (phone) => {
    try {
        const parsed = parsePhoneNumber(phone);

        return (
            parsed?.nationalNumber ||
            String(phone || "").replace(/\D/g, "")
        );
    } catch (_) {
        return String(phone || "").replace(/\D/g, "");
    }
};

const isFakePhoneNumber = (phone) => {
    const nationalNumber = getNationalPhoneNumber(phone);

    if (!nationalNumber || nationalNumber.length < 8) {
        return true;
    }

    if (/^(\d)\1+$/.test(nationalNumber)) {
        return true;
    }

    const blockedNumbers = [
        "0000000000",
        "1111111111",
        "2222222222",
        "3333333333",
        "4444444444",
        "5555555555",
        "6666666666",
        "7777777777",
        "8888888888",
        "9999999999",
        "1234567890",
        "0123456789",
        "9876543210",
    ];

    if (blockedNumbers.includes(nationalNumber)) {
        return true;
    }

    const ascending = "01234567890123456789";
    const descending = "98765432109876543210";

    return (
        ascending.includes(nationalNumber) ||
        descending.includes(nationalNumber)
    );
};

const isValidSignupPhone = (phone) => {
    return Boolean(
        phone &&
        isValidPhoneNumber(phone) &&
        !isFakePhoneNumber(phone)
    );
};

const isValidWebsite = (website) => {
    const value = String(website || "").trim();

    if (!value) return false;

    try {
        const normalized = /^https?:\/\//i.test(value)
            ? value
            : `https://${value}`;

        const url = new URL(normalized);

        return Boolean(
            ["http:", "https:"].includes(url.protocol) &&
            url.hostname.includes(".")
        );
    } catch (_) {
        return false;
    }
};

export default function Signup() {
    const initialPendingGoogleSetup =
        readPendingGoogleSetup();

    const [pendingGoogleSetup, setPendingGoogleSetup] =
        useState(initialPendingGoogleSetup);

    const [step, setStep] = useState(
        initialPendingGoogleSetup ? 3 : 1
    );

    const [details, setDetails] = useState(() =>
        getInitialDetails(initialPendingGoogleSetup)
    );

    const [mobile, setMobile] = useState();
    const [isLoader, setIsLoader] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();

    const antIcon = (
        <LoadingOutlined
            style={{ fontSize: 24, color: "#fff" }}
            spin
        />
    );

    useEffect(() => {
        saveMiniToolRedirect(location.search);

        const params = new URLSearchParams(
            location.search
        );
        const emailFromQuery = params.get("email");

        if (!pendingGoogleSetup && emailFromQuery) {
            setDetails((previous) => ({
                ...previous,
                email: emailFromQuery,
            }));
        }
    }, [location.search, pendingGoogleSetup]);

    const savePendingGoogleSetup = (setupData) => {
        sessionStorage.setItem(
            PENDING_GOOGLE_SETUP_KEY,
            JSON.stringify(setupData)
        );

        setPendingGoogleSetup(setupData);
        setDetails(getInitialDetails(setupData));
        setStep(3);
    };

    const handleGoogleSuccess = async (
        credentialResponse
    ) => {
        setIsLoader(true);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_API}/user/auth/google`,
                {
                    token: credentialResponse.credential,
                }
            );

            const {
                requiresBusinessSetup,
                googleSetupToken,
                googleUser,
                token,
                user,
            } = response.data.data;

            if (requiresBusinessSetup) {
                localStorage.removeItem("user_Token");
                sessionStorage.removeItem("user_Token");
                localStorage.removeItem("user_id");

                savePendingGoogleSetup({
                    googleSetupToken,
                    user: googleUser,
                });

                message.success(
                    "Google authentication successful. Complete your company details."
                );

                return;
            }

            if (!token || !user) {
                throw new Error(
                    "Google authentication returned an invalid response."
                );
            }

            localStorage.setItem("user_Token", token);
            localStorage.setItem("Email", user.email_id);
            localStorage.setItem("user_id", user._id);

            sessionStorage.removeItem(
                PENDING_GOOGLE_SETUP_KEY
            );

            message.success("Authentication successful!");
            window.location.href =
                getPostAuthRedirect("/Dashboard");
        } catch (error) {
            toast.error(
                error.response?.data?.message ||
                error.message ||
                "Google Sign-In failed."
            );
        } finally {
            setIsLoader(false);
        }
    };

    const handleGoogleError = () => {
        toast.error(
            "Google authentication failed. Please try again."
        );
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;

        setDetails((previous) => ({
            ...previous,
            [name]: value,
        }));
    };

    const handleNextStep = async (event) => {
        event.preventDefault();

        const emailRegex =
            /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (
            !details.email.trim() ||
            !emailRegex.test(details.email)
        ) {
            toast.error("Invalid email format.");
            return;
        }

        setIsLoader(true);

        try {
            const emailCheck = await checkEmailExists(
                details.email
            );

            if (emailCheck.exists) {
                toast.error(
                    "This email is already registered. Please log in."
                );
                return;
            }

            setStep(2);
        } catch (error) {
            toast.error(
                "Unable to verify email. Please try again."
            );
        } finally {
            setIsLoader(false);
        }
    };

    const handleEmailRegister = async (event) => {
        event.preventDefault();

        if (
            !details.firstname ||
            !details.lastname ||
            !details.company_name ||
            !details.website ||
            !details.password ||
            !mobile
        ) {
            toast.error("All fields are required.");
            return;
        }

        if (!isValidWebsite(details.website)) {
            toast.error(
                "Please enter a valid website URL."
            );
            return;
        }

        if (!isValidSignupPhone(mobile)) {
            toast.error(
                "Please enter a valid phone number."
            );
            return;
        }

        setIsLoader(true);

        try {
            await axios.post(
                `${process.env.REACT_APP_BACKEND_API}/user/register`,
                {
                    first_name: details.firstname.trim(),
                    last_name: details.lastname.trim(),
                    email_id: details.email.trim(),
                    password: details.password,
                    mobile,
                    company_name:
                        details.company_name.trim(),
                    website: details.website.trim(),
                }
            );

            localStorage.setItem(
                "Email",
                details.email.trim()
            );

            toast.success(
                "Account created! Verifying your email..."
            );

            navigate("/verify-otp");
        } catch (error) {
            toast.error(
                error.response?.data?.message ||
                "Registration failed. Please try again."
            );
        } finally {
            setIsLoader(false);
        }
    };

    const handleGoogleRegister = async (event) => {
        event.preventDefault();

        if (!pendingGoogleSetup?.googleSetupToken) {
            toast.error(
                "Your Google signup session is missing. Please sign in with Google again."
            );

            sessionStorage.removeItem(
                PENDING_GOOGLE_SETUP_KEY
            );

            setPendingGoogleSetup(null);
            setStep(1);
            return;
        }

        if (
            !details.company_name.trim() ||
            !details.website.trim() ||
            !mobile
        ) {
            toast.error(
                "Company Name, Website, and Phone Number are required."
            );
            return;
        }

        if (!isValidWebsite(details.website)) {
            toast.error(
                "Please enter a valid website URL."
            );
            return;
        }

        if (!isValidSignupPhone(mobile)) {
            toast.error(
                "Please enter a valid phone number."
            );
            return;
        }

        setIsLoader(true);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_API}/user/auth/google/complete`,
                {
                    googleSetupToken:
                        pendingGoogleSetup.googleSetupToken,
                    company_name:
                        details.company_name.trim(),
                    website: details.website.trim(),
                    mobile,
                }
            );

            const { token, user } = response.data.data;

            if (!token || !user) {
                throw new Error(
                    "Signup completed without a valid login session."
                );
            }

            localStorage.setItem("user_Token", token);
            localStorage.setItem("Email", user.email_id);
            localStorage.setItem("user_id", user._id);

            sessionStorage.removeItem(
                PENDING_GOOGLE_SETUP_KEY
            );

            setPendingGoogleSetup(null);

            toast.success(
                "Account and brand created successfully."
            );

            window.location.href =
                getPostAuthRedirect("/Dashboard");
        } catch (error) {
            if (error.response?.status === 401) {
                sessionStorage.removeItem(
                    PENDING_GOOGLE_SETUP_KEY
                );

                setPendingGoogleSetup(null);
                setStep(1);
            }

            toast.error(
                error.response?.data?.message ||
                error.message ||
                "Failed to complete signup. Please try again."
            );
        } finally {
            setIsLoader(false);
        }
    };

    const renderGoogleBusinessSetup = () => (
        <form
            onSubmit={handleGoogleRegister}
            className="space-y-4"
        >
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-neutral-muted mb-1">
                        First Name
                    </label>
                    <input
                        type="text"
                        className="form-input bg-gray-200"
                        value={details.firstname}
                        readOnly
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-neutral-muted mb-1">
                        Last Name
                    </label>
                    <input
                        type="text"
                        className="form-input bg-gray-200"
                        value={details.lastname}
                        readOnly
                    />
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-neutral-muted mb-1">
                    Company Name
                </label>
                <input
                    type="text"
                    name="company_name"
                    value={details.company_name}
                    className="form-input"
                    onChange={handleInputChange}
                    autoFocus
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-neutral-muted mb-1">
                    Company Website
                </label>
                <input
                    type="text"
                    name="website"
                    value={details.website}
                    placeholder="e.g. oolook.in"
                    className="form-input"
                    onChange={handleInputChange}
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-neutral-muted mb-1">
                    Phone Number
                </label>
                <PhoneInput
                    defaultCountry="IN"
                    value={mobile}
                    onChange={setMobile}
                    className="form-input"
                />
            </div>

            <div className="pt-4">
                <button
                    type="submit"
                    className="btn-primary"
                    disabled={isLoader}
                >
                    {isLoader ? (
                        <Spin indicator={antIcon} />
                    ) : (
                        "Create Account"
                    )}
                </button>
            </div>

            <p className="text-xs text-center text-neutral-muted px-4">
                By continuing, you agree to the{" "}
                <a
                    href="https://oolook.in/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link"
                >
                    Terms of Service
                </a>{" "}
                and{" "}
                <a
                    href="https://oolook.in/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link"
                >
                    Privacy Policy
                </a>
                .
            </p>
        </form>
    );

    return (
        <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-neutral-light">
            <ToastContainer
                position="top-right"
                autoClose={5000}
            />

            <div className="auth-card">
                <img
                    src={HeadLogo}
                    alt="Oolook Logo"
                    className="mx-auto h-12 w-auto mb-6"
                />

                <h2 className="text-center text-2xl font-bold text-neutral-dark mb-2">
                    {step === 1 && "Get Started"}
                    {step === 2 &&
                        "Tell us about yourself"}
                    {step === 3 &&
                        "Complete your signup"}
                </h2>

                <p className="text-center text-sm text-neutral-muted mb-8">
                    {step === 1 &&
                        "Create your account to get started."}
                    {step === 2 &&
                        "Complete your profile to continue."}
                    {step === 3 &&
                        "Add your company details to create your account and first brand."}
                </p>

                {step === 1 && (
                    <>
                        <div className="flex justify-center">
                            <GoogleLogin
                                onSuccess={
                                    handleGoogleSuccess
                                }
                                onError={handleGoogleError}
                                text="signup_with"
                            />
                        </div>

                        <Divider className="my-6">
                            OR
                        </Divider>
                    </>
                )}

                {step === 3 ? (
                    renderGoogleBusinessSetup()
                ) : (
                    <form
                        onSubmit={
                            step === 1
                                ? handleNextStep
                                : handleEmailRegister
                        }
                        className="space-y-4"
                    >
                        {step === 1 && (
                            <div>
                                <label className="block text-sm font-medium text-neutral-muted mb-1">
                                    Email Address
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    value={details.email}
                                    className="form-input"
                                    onChange={
                                        handleInputChange
                                    }
                                    autoFocus
                                />
                            </div>
                        )}

                        {step === 2 && (
                            <>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-neutral-muted mb-1">
                                            First Name
                                        </label>
                                        <input
                                            type="text"
                                            name="firstname"
                                            value={
                                                details.firstname
                                            }
                                            className="form-input"
                                            onChange={
                                                handleInputChange
                                            }
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-neutral-muted mb-1">
                                            Last Name
                                        </label>
                                        <input
                                            type="text"
                                            name="lastname"
                                            value={
                                                details.lastname
                                            }
                                            className="form-input"
                                            onChange={
                                                handleInputChange
                                            }
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-neutral-muted mb-1">
                                        Company Name
                                    </label>
                                    <input
                                        type="text"
                                        name="company_name"
                                        value={
                                            details.company_name
                                        }
                                        className="form-input"
                                        onChange={
                                            handleInputChange
                                        }
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-neutral-muted mb-1">
                                        Company Website
                                    </label>
                                    <input
                                        type="text"
                                        name="website"
                                        value={
                                            details.website
                                        }
                                        placeholder="e.g. oolook.in"
                                        className="form-input"
                                        onChange={
                                            handleInputChange
                                        }
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-neutral-muted mb-1">
                                        Phone Number
                                    </label>
                                    <PhoneInput
                                        defaultCountry="IN"
                                        value={mobile}
                                        onChange={
                                            setMobile
                                        }
                                        className="form-input"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-neutral-muted mb-1">
                                        Password
                                    </label>

                                    <div className="relative">
                                        <input
                                            type={
                                                showPassword
                                                    ? "text"
                                                    : "password"
                                            }
                                            name="password"
                                            value={
                                                details.password
                                            }
                                            className="form-input pr-10"
                                            onChange={
                                                handleInputChange
                                            }
                                        />

                                        <button
                                            type="button"
                                            onClick={() =>
                                                setShowPassword(
                                                    !showPassword
                                                )
                                            }
                                            className="absolute inset-y-0 right-0 px-3 flex items-center text-neutral-muted"
                                        >
                                            <i
                                                className={`fa-regular ${
                                                    showPassword
                                                        ? "fa-eye-slash"
                                                        : "fa-eye"
                                                }`}
                                            ></i>
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}

                        <div className="pt-4">
                            <button
                                type="submit"
                                className="btn-primary"
                                disabled={isLoader}
                            >
                                {isLoader ? (
                                    <Spin
                                        indicator={
                                            antIcon
                                        }
                                    />
                                ) : step === 1 ? (
                                    "Next"
                                ) : (
                                    "Create Account"
                                )}
                            </button>
                        </div>

                        <p className="text-xs text-center text-neutral-muted px-4">
                            By signing up, you agree to
                            the{" "}
                            <a
                                href="https://oolook.in/terms"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="link"
                            >
                                Terms of Service
                            </a>{" "}
                            and{" "}
                            <a
                                href="https://oolook.in/privacy"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="link"
                            >
                                Privacy Policy
                            </a>
                            .
                        </p>

                        {step === 2 && (
                            <button
                                type="button"
                                onClick={() =>
                                    setStep(1)
                                }
                                className="w-full text-center text-sm text-neutral-muted mt-2 hover:text-primary"
                            >
                                Back to Email
                            </button>
                        )}
                    </form>
                )}

                {step !== 3 && (
                    <p className="text-xs text-center text-neutral-muted mt-6">
                        Already have an account?{" "}
                        <Link
                            to="/login"
                            className="link"
                        >
                            Log In
                        </Link>
                    </p>
                )}
            </div>
        </div>
    );
}